"use client";

import { errorHandlerReport } from "@mgdx-libs/error-handler";
import { getFirebaseFirestore, useDocument } from "@mgdx-libs/firebase";
import { doc, updateDoc } from "firebase/firestore";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useRef } from "react";

import { ROOM_COLLECTION_NAME } from "../constants";
import { roomConverter, TalkRoomDocumentWithId } from "../models/TalkRoomDocument";
import { TalkUnreadCountMismatchError } from "../models/TalkUnreadCountMismatchError";

export type UseTalkRoomDocumentProps = {
  roomId?: string;
};

export type UseTalkRoomDocument = (props: UseTalkRoomDocumentProps) => {
  room?: TalkRoomDocumentWithId;
  isLoading: boolean;
};

const getRoomRef = (roomId: string) =>
  doc(getFirebaseFirestore(), ROOM_COLLECTION_NAME, roomId).withConverter(roomConverter);

type RoomRef = ReturnType<typeof getRoomRef>;

const reportUnreadCountMismatch = (message: string) => {
  try {
    throw new TalkUnreadCountMismatchError(message);
  } catch (e) {
    if (typeof e === "string" || e instanceof Error) {
      errorHandlerReport(e);
    } else {
      errorHandlerReport("Unknown error occurred");
    }
  }
};

export const useTalkRoomDocument: UseTalkRoomDocument = ({ roomId }) => {
  // roomId が無いときは購読しない(存在しないドキュメントの購読は Security Rules で拒否されるため)
  const roomRef = useMemo(() => (roomId ? getRoomRef(roomId) : null), [roomId]);
  const latestRoomRef = useRef(roomRef);

  const updateMedicalUnreadMessageCountRef = useRef(
    debounce((targetRoomRef: RoomRef, medicalUnreadMessageCount: number) => {
      // 予約後に roomId が切り替わっていたら書き込まない
      if (latestRoomRef.current?.path !== targetRoomRef.path) return;

      updateDoc(targetRoomRef, {
        medicalUnreadMessageCount,
      }).catch(errorHandlerReport);
      reportUnreadCountMismatch(
        `Medical unread message counts do not match. Correct the count to ${medicalUnreadMessageCount}.`
      );
    }, 200)
  );

  const updatePatientUnreadMessageCountRef = useRef(
    debounce((targetRoomRef: RoomRef, patientUnreadMessageCount: number) => {
      // 予約後に roomId が切り替わっていたら書き込まない
      if (latestRoomRef.current?.path !== targetRoomRef.path) return;

      updateDoc(targetRoomRef, {
        patientUnreadMessageCount,
      }).catch(errorHandlerReport);
      reportUnreadCountMismatch(
        `Patient unread message counts do not match. Correct the count to ${patientUnreadMessageCount}.`
      );
    }, 200)
  );

  useEffect(() => {
    latestRoomRef.current = roomRef;

    const updateMedicalUnreadMessageCount = updateMedicalUnreadMessageCountRef.current;
    const updatePatientUnreadMessageCount = updatePatientUnreadMessageCountRef.current;
    return () => {
      updateMedicalUnreadMessageCount.cancel();
      updatePatientUnreadMessageCount.cancel();
    };
  }, [roomRef]);

  const [room, isLoading] = useDocument(roomRef);
  // roomId 切り替え直後は前のトークルームのスナップショットが返るため、現在の roomId のものだけを使う
  const roomData = room?.id === roomId ? (room?.data() as TalkRoomDocumentWithId | undefined) : undefined;

  const data = useMemo(
    () => ({
      room: roomData,
      isLoading,
    }),
    [isLoading, roomData]
  );

  useEffect(() => {
    if (!roomRef) return;
    if (!data.room) return;
    if (data.isLoading) return;

    const medicalUnreadMessageIds = data.room.medicalUnreadMessageIds;
    if (
      Array.isArray(medicalUnreadMessageIds) &&
      data.room.medicalUnreadMessageCount !== medicalUnreadMessageIds.length
    ) {
      updateMedicalUnreadMessageCountRef.current(roomRef, medicalUnreadMessageIds.length);
    }

    const patientUnreadMessageIds = data.room.patientUnreadMessageIds;
    if (
      Array.isArray(patientUnreadMessageIds) &&
      data.room.patientUnreadMessageCount !== patientUnreadMessageIds.length
    ) {
      updatePatientUnreadMessageCountRef.current(roomRef, patientUnreadMessageIds.length);
    }
  }, [data, roomRef]);

  return data;
};
